import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Typography, Checkbox } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { usePermissionStore } from '../stores/permissionStore';
import { authService } from '../services/auth';
import { storageService } from '../services/storage';
import { initializeDeviceConstantsOnLogin } from '../hooks/useDeviceConstantsStore';
import Turnstile from '../components/Turnstile';

const { Title } = Typography;

interface LoginForm {
  username: string;
  password: string;
  remember: boolean;
}

const CUSTOMER_SERVICE_URL = 'https://t.me/webpage1688';
const CUSTOMER_SERVICE_HANDLE = '@webpage1688';

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showCustomerService, setShowCustomerService] = useState(false);
  const [turnstileVerified, setTurnstileVerified] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { loadPermissions, loadMenuPermissions } = usePermissionStore();

  // 页面加载时检查是否有记住的凭据
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      setShowCustomerService(
        hostname === 'console.ipcloak.ai' || hostname.endsWith('.ipcloak.ai') ||
        hostname === 'ipcloak.icu' || hostname.endsWith('.ipcloak.icu')
      );
    }

    const hasRemembered = storageService.hasRememberedCredentials();
    const rememberPassword = storageService.getRememberPassword();
    
    if (hasRemembered && rememberPassword) {
      const rememberedUsername = storageService.getRememberedUsername();
      const rememberedPassword = storageService.getRememberedPassword();
      
      form.setFieldsValue({
        username: rememberedUsername,
        password: rememberedPassword,
        remember: true,
      });
    }
  }, [form]);

  const onFinish = async (values: LoginForm) => {
    setLoading(true);
    try {
      const { remember, ...loginData } = values;
      
      const response = await authService.login(loginData);
      const { user, token, permissions, roles } = response;
      
      console.log('登录响应:', { user, token, permissions, roles });
      
      // 处理记住密码
      if (remember) {
        storageService.saveRememberPassword(true);
        storageService.saveRememberedCredentials(values.username, values.password);
      } else {
        storageService.saveRememberPassword(false);
        storageService.clearRememberedCredentials();
      }
      
      // 登录成功，保存用户信息和token
      login(user, token);
      
      // 初始化设备常量
      try {
        console.log('🔧 开始初始化设备常量...');
        await initializeDeviceConstantsOnLogin();
        console.log('✅ 设备常量初始化成功');
      } catch (error) {
        console.error('❌ 设备常量初始化失败:', error);
        // 不阻止登录流程，只记录错误
      }
      
      // 如果后端返回了权限数据，直接使用
      if (permissions && permissions.length > 0) {
        console.log('使用后端返回的权限数据:', permissions);
        // 直接设置权限数据到store，避免App.tsx重复加载
        usePermissionStore.getState().setPermissions(permissions);
      }
      // 注意：不在这里加载菜单权限，让App.tsx统一处理权限加载
      
      message.success('登录成功');
      navigate('/dashboard');
    } catch (error: any) {
      const errMsg = error?.response?.data?.message || error.message || '登录失败';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      {showCustomerService && (
        <>
          <style>
            {`
              @keyframes loginPageMarquee {
                0% { transform: translateX(100%); }
                100% { transform: translateX(-100%); }
              }
            `}
          </style>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              background: 'rgba(0, 0, 0, 0.65)',
              color: '#fff',
              fontSize: 12,
              padding: '4px 0',
              zIndex: 1000,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                paddingLeft: '100%',
                animation: 'loginPageMarquee 15s linear infinite',
              }}
            >
              请合法使用本平台，严禁将其用于任何非法业务
            </span>
          </div>
        </>
      )}
      <Card style={{ width: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            访客管理平台
          </Title>
          <p style={{ color: '#666', margin: '8px 0 0 0' }}>
            请登录您的账户
          </p>
        </div>
        
        <Form
          name="login"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: '请输入用户名!' }]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="用户名" 
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码!' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
            />
          </Form.Item>

          <Form.Item name="remember" valuePropName="checked">
            <Checkbox>记住密码</Checkbox>
          </Form.Item>

          <Turnstile
            onVerify={() => setTurnstileVerified(true)}
            onExpire={() => setTurnstileVerified(false)}
            onError={() => setTurnstileVerified(false)}
            onUnsupported={() => setTurnstileVerified(true)}
          />

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              disabled={!turnstileVerified}
              style={{ width: '100%' }}
            >
              {turnstileVerified ? '登录' : '人机验证中...'}
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <Typography.Link onClick={() => navigate('/register')}>
              还没有账号？注册账号
            </Typography.Link>
          </div>

          {showCustomerService && (
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Typography.Link
                href={CUSTOMER_SERVICE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                联系客服 Telegram：{CUSTOMER_SERVICE_HANDLE}
              </Typography.Link>
              <Typography.Link
                href="https://t.me/ipcloak"
                target="_blank"
                rel="noopener noreferrer"
              >
                加入交流群：@ipcloak
              </Typography.Link>
            </div>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default LoginPage;